import React, { useRef } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useDispatch, useSelector } from 'react-redux';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useUploadAskAiDocumentMutation } from '@/features/askAi/askAiApi';
import { showToast } from '@/features/toast/toastSlice';
import { useAskAIContext } from '@/context/AskAiContext';

const UploadFileButton = () => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const { isBusy, refreshMessages, session, setLocalBusy } = useAskAIContext();
	const sessionId = session?.sessionId ?? session?.id;

	const dialogData = useSelector((state) => state.dialog?.data?.askAi) ?? {};

	const { clientRow, dealRow } = useObjectContext();

	const resolvedClient = dialogData.client ?? clientRow ?? null;
	const resolvedDeal = dialogData.deal ?? dealRow ?? null;

	const contextType = dialogData.contextType ?? (resolvedDeal ? 'deal' : 'client');
	const isClientContext = contextType === 'client';

	const uploadInputRef = useRef(null);

	const resolveSharepointTarget = async () => {
		if (isClientContext) {
			return getClientDriveId(resolvedClient?.clientId);
		}
		return getDealDriveId(resolvedDeal?.dealId);
	};

	const { getClientDriveId, getDealDriveId } = useClientStorageHooks(
		resolvedDeal?.brokerGroupId ?? resolvedClient?.brokerGroupId,
	);
	const { uploadFilesAndRetry } = useMsGraph();

	const [uploadDocument] = useUploadAskAiDocumentMutation({ fixedCacheKey: 'ask-ai' });

	const readFileAsBase64 = (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = String(reader.result ?? '');
				const [, base64 = ''] = result.split(',');
				resolve(base64);
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});

	const handleUploadFiles = async (event) => {
		const files = Array.from(event.target.files ?? []);
		if (!sessionId || files.length === 0) return;

		setLocalBusy(true);
		try {
			const sharepointTarget = await resolveSharepointTarget();
			const resolvedFiles = [];

			if (sharepointTarget?.driveId && sharepointTarget?.clientFolderDriveId) {
				await uploadFilesAndRetry({
					driveId: sharepointTarget.clientFolderDriveId,
					itemId: sharepointTarget.driveId,
					files,
					silent: true,
				});
			}

			for (const file of files) {
				const base64 = await readFileAsBase64(file);
				await uploadDocument({
					sessionId,
					fileName: file.name,
					contentType: file.type,
					base64,
					sharepointDriveId: sharepointTarget?.clientFolderDriveId ?? null,
					sharepointItemId: sharepointTarget?.driveId ?? null,
				}).unwrap();
				resolvedFiles.push(file);
			}

			dispatch(
				showToast({
					message: `Uploaded ${resolvedFiles.length} documents to Folio AI; you can now ask questions about these documents.`,
					success: true,
				}),
			);
			await refreshMessages();
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.error ?? 'Could not upload Folio AI documents.',
					error: true,
				}),
			);
		} finally {
			event.target.value = '';
			setLocalBusy(false);
		}
	};

	return (
		<Box>
			<input ref={uploadInputRef} type='file' multiple hidden onChange={handleUploadFiles} />
			<Button
				startIcon={<UploadFileIcon />}
				variant='text'
				color='inherit'
				onClick={() => uploadInputRef.current?.click()}
				disabled={!sessionId || isBusy || Boolean(session?.disabledReason)}
				sx={{
					textTransform: 'none',
					px: 1.8,
					color: theme.palette.text.secondary,
				}}
			>
				Upload file
			</Button>
		</Box>
	);
};

export default UploadFileButton;
